import { Forma } from 'forma-embedded-view-sdk/auto';

/**
 * Storage service wrapper for Forma Extension Storage API
 * Provides methods for storing key-value pairs and JSON objects as text
 */
class FormaStorageService {
	private storage = Forma.extensions.storage;

	/**
	 * Store a simple key-value pair as text
	 * @param key The key to store the value under
	 * @param value The value to store (will be converted to string)
	 * @param authcontext Optional authcontext (defaults to current project)
	 * @returns Promise that resolves when the value is stored
	 */
	async setItem(
		key: string,
		value: string | number | boolean,
		authcontext?: string
	): Promise<void> {
		try {
			const stringValue = String(value);
			await this.storage.setObject({ key, data: stringValue, authcontext });
		} catch (error) {
			console.error(`Error setting item with key "${key}":`, error);
			throw new Error(`Failed to set item with key "${key}": ${error}`);
		}
	}

	/**
	 * Retrieve a simple value by key
	 * @param key The key of the value to retrieve
	 * @param authcontext Optional authcontext (defaults to current project)
	 * @returns Promise that resolves to the stored value or null if not found
	 */
	async getItem(key: string, authcontext?: string): Promise<string | null> {
		try {
			const result = await this.storage.getTextObject({ key, authcontext });
			return result?.data ?? null;
		} catch (error) {
			console.error(`Error getting item with key "${key}":`, error);
			throw new Error(`Failed to get item with key "${key}": ${error}`);
		}
	}

	/**
	 * Store a JSON object
	 * @param key The key to store the object under
	 * @param value The object to store (will be serialized to JSON)
	 * @param authcontext Optional authcontext (defaults to current project)
	 * @returns Promise that resolves when the object is stored
	 */
	async setJSON<T>(key: string, value: T, authcontext?: string): Promise<void> {
		try {
			const jsonString = JSON.stringify(value);
			await this.storage.setObject({ key, data: jsonString, authcontext });
		} catch (error) {
			console.error(`Error setting JSON object with key "${key}":`, error);
			throw new Error(`Failed to set JSON object with key "${key}": ${error}`);
		}
	}

	/**
	 * Retrieve a JSON object by key
	 * @param key The key of the object to retrieve
	 * @param authcontext Optional authcontext (defaults to current project)
	 * @returns Promise that resolves to the parsed object or null if not found
	 */
	async getJSON<T>(key: string, authcontext?: string): Promise<T | null> {
		try {
			const result = await this.storage.getTextObject({ key, authcontext });
			if (!result) return null;

			try {
				return JSON.parse(result.data) as T;
			} catch (parseError) {
				console.error(`Error parsing JSON for key "${key}":`, parseError);
				throw new Error(`Failed to parse JSON for key "${key}": ${parseError}`);
			}
		} catch (error) {
			console.error(`Error getting JSON object with key "${key}":`, error);
			throw new Error(`Failed to get JSON object with key "${key}": ${error}`);
		}
	}

	/**
	 * Delete an item by key
	 * @param key The key of the item to delete
	 * @param authcontext Optional authcontext (defaults to current project)
	 * @returns Promise that resolves when the item is deleted
	 */
	async removeItem(key: string, authcontext?: string): Promise<void> {
		try {
			await this.storage.deleteObject({ key, authcontext });
		} catch (error) {
			console.error(`Error deleting item with key "${key}":`, error);
			throw new Error(`Failed to delete item with key "${key}": ${error}`);
		}
	}

	/**
	 * List all stored objects with optional prefix filter
	 * @param prefix Optional prefix to filter objects by key
	 * @param authcontext Optional authcontext (defaults to current project)
	 * @returns Promise that resolves to an array of object information
	 */
	async listItems(
		prefix?: string,
		authcontext?: string
	): Promise<{ key: string; lastModified: string; size: number }[]> {
		try {
			const result = await this.storage.listObjects({ prefix, authcontext });
			return result.results.map((item) => ({
				key: item.key,
				lastModified: item.lastModified,
				size: item.size,
			}));
		} catch (error) {
			console.error('Error listing items:', error);
			throw new Error(`Failed to list items: ${error}`);
		}
	}
}

// Create and export a singleton instance
export const formaStorageService = new FormaStorageService();
